export const MAX_TOTAL_MB = 50
export const MAX_TOTAL_BYTES = MAX_TOTAL_MB * 1024 * 1024

const SUPPORTED_RASTER_TYPES = ['image/jpeg', 'image/png'] as const
const SUPPORTED_HEIC_TYPES = [
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
] as const

type SupportedRasterType = (typeof SUPPORTED_RASTER_TYPES)[number]
type SupportedHeicType = (typeof SUPPORTED_HEIC_TYPES)[number]

export type ImageToPdfMode = 'jpg' | 'png' | 'heic'
export type ImagesToPdfErrorCode = 'unsupported_browser' | 'conversion_error'

export class ImagesToPdfError extends Error {
  constructor(public readonly code: ImagesToPdfErrorCode) {
    super(code)
    this.name = 'ImagesToPdfError'
  }
}

export interface ImageFileItem {
  id: string
  file: File
  previewUrl: string | null
}

export function isHeicFile(file: File): boolean {
  const lowerName = file.name.toLowerCase()
  return (
    SUPPORTED_HEIC_TYPES.includes(file.type as SupportedHeicType) ||
    lowerName.endsWith('.heic') ||
    lowerName.endsWith('.heif')
  )
}

export function isSupportedImage(file: File, mode: ImageToPdfMode): boolean {
  const lowerName = file.name.toLowerCase()
  if (mode === 'heic') {
    return isHeicFile(file)
  }

  return (
    SUPPORTED_RASTER_TYPES.includes(file.type as SupportedRasterType) ||
    lowerName.endsWith('.jpg') ||
    lowerName.endsWith('.jpeg') ||
    lowerName.endsWith('.png')
  )
}

export function totalBytes(items: ImageFileItem[]): number {
  return items.reduce((sum, item) => sum + item.file.size, 0)
}

export function outputFilename(items: ImageFileItem[], fallback: string): string {
  const first = items[0]
  if (items.length === 1 && first) {
    return `${first.file.name.replace(/\.[^.]+$/, '') || 'image'}.pdf`
  }
  return fallback
}

function assertHeicDecoderSupport() {
  if (
    typeof window === 'undefined' ||
    typeof Worker === 'undefined' ||
    typeof WebAssembly === 'undefined'
  ) {
    throw new ImagesToPdfError('unsupported_browser')
  }
}

async function heicToJpegBytes(file: File): Promise<Uint8Array> {
  assertHeicDecoderSupport()

  try {
    const { default: heic2any } = await import('heic2any')
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92,
    })
    const jpegBlob = Array.isArray(converted) ? converted[0] : converted
    if (!jpegBlob) {
      throw new ImagesToPdfError('conversion_error')
    }
    return new Uint8Array(await jpegBlob.arrayBuffer())
  } catch (error) {
    if (error instanceof ImagesToPdfError) {
      throw error
    }
    throw new ImagesToPdfError('conversion_error')
  }
}

async function embeddableBytes(file: File, mode: ImageToPdfMode): Promise<{
  bytes: Uint8Array
  kind: 'jpg' | 'png'
}> {
  if (mode === 'heic' || isHeicFile(file)) {
    return {
      bytes: await heicToJpegBytes(file),
      kind: 'jpg',
    }
  }

  return {
    bytes: new Uint8Array(await file.arrayBuffer()),
    kind: file.type === 'image/png' || file.name.toLowerCase().endsWith('.png') ? 'png' : 'jpg',
  }
}

export async function imagesToPdf(files: File[], mode: ImageToPdfMode): Promise<Blob> {
  const { PDFDocument } = await import('pdf-lib')
  const pdf = await PDFDocument.create()

  try {
    for (const file of files) {
      const imageBytes = await embeddableBytes(file, mode)
      const image =
        imageBytes.kind === 'png'
          ? await pdf.embedPng(imageBytes.bytes)
          : await pdf.embedJpg(imageBytes.bytes)

      const page = pdf.addPage([image.width, image.height])
      page.drawImage(image, {
        x: 0,
        y: 0,
        width: image.width,
        height: image.height,
      })
    }

    const pdfBytes = await pdf.save()
    return new Blob([pdfBytes], { type: 'application/pdf' })
  } catch (error) {
    if (error instanceof ImagesToPdfError) {
      throw error
    }
    throw new ImagesToPdfError('conversion_error')
  }
}
